/*
 * Photo Headshot — reads the selected image shape's geometry and captures its
 * pixel data.
 *
 * Mechanics (Office.js selection read + slide-capture-and-crop) live in
 * shared/slideImageCapture.ts, shared with sketchToSlideSelection.ts. Only
 * this feature's message copy stays here.
 */

import type { SlideImageMessages, SlideImageReasons } from "../../shared/services/slideImageCapture";
import { captureSelectedImage, readSelectedImageShape } from "../../shared/services/slideImageCapture";
import { logPhotoHeadError } from "./photoHeadApi";
import type { SelectedPhotoShape } from "../../../types";

export const PPT_ONLY_MESSAGE = "This tool works only in PowerPoint.";
export const NO_SELECTION_MESSAGE = "Select a portrait image on the slide first.";
export const MULTI_SELECTION_MESSAGE = "Select just one image — only one photo can become a photo head at a time.";
export const NOT_AN_IMAGE_MESSAGE = "The selected shape isn't a picture. Select a portrait image instead.";
export const CAPTURE_UNSUPPORTED_MESSAGE =
  "Reading the selected photo needs PowerPointApi 1.5+ — try PowerPoint on the web, or update desktop PowerPoint.";

const MESSAGES: SlideImageMessages = {
  pptOnly: PPT_ONLY_MESSAGE,
  noSelection: NO_SELECTION_MESSAGE,
  multiSelection: MULTI_SELECTION_MESSAGE,
  notAnImage: NOT_AN_IMAGE_MESSAGE,
  captureUnsupported: CAPTURE_UNSUPPORTED_MESSAGE,
  itemLabel: "photo",
};

const REASONS: SlideImageReasons = {
  placeholderTypeCheck: "photo-head-placeholder-type-check",
  slideSizeRead: "photo-head-slide-size-read",
  slideCapture: "photo-head-slide-capture",
  cropEncode: "photo-head-crop-encode",
  cropEmptyResult: "photo-head-crop-empty-result",
};

/** Read the current selection: exactly one image/picture shape, with its geometry. */
export function readSelectedPhotoShape(): Promise<SelectedPhotoShape> {
  return readSelectedImageShape(MESSAGES, logPhotoHeadError, REASONS);
}

/** Capture the selected image shape's pixels as a base64 PNG. */
export function captureSelectedPhoto(shape: SelectedPhotoShape): Promise<{ base64: string; mimeType: string }> {
  return captureSelectedImage(shape, MESSAGES, logPhotoHeadError, REASONS);
}
